import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Chip,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BuildIcon from '@mui/icons-material/Build';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

function ApplyPackageDialog({ open, handleClose, maintenance, setMaintenance }) {
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const autoCloseTimerRef = useRef(null);

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { is_active: true };
      if (search) params.search = search;
      const response = await axios.get('/api/service-packages/', { params });
      const data = response.data.results || response.data;
      setPackages(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(t('huoltopaketit.applyError'));
    } finally {
      setLoading(false);
    }
  }, [search, t]);

  useEffect(() => {
    if (!open) return;
    setSelectedPackage(null);
    setError(null);
    setSuccess(null);
    setSearch('');
    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => fetchPackages(), 200);
    return () => clearTimeout(timer);
  }, [open, fetchPackages]);

  const handleApply = async () => {
    if (!selectedPackage) return;
    setApplying(true);
    setError(null);
    try {
      const response = await axios.post(`/api/maintenances/${maintenance.id}/apply-package/`, {
        package_id: selectedPackage.id,
      });
      setMaintenance((prev) => ({ ...prev, ...response.data }));
      setSuccess(t('huoltopaketit.applied', {
        name: selectedPackage.name,
      }));
      autoCloseTimerRef.current = setTimeout(() => handleClose(), 1200);
    } catch (err) {
      setError(err.response?.data?.error || t('huoltopaketit.applyError'));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Inventory2Icon sx={{ color: 'secondary.main' }} />
          <Typography
            sx={{
              fontSize: '18px',
              fontWeight: 600,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {t('huoltopaketit.applyTitle')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 1 }}>
        {success ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
            <Typography sx={{ fontWeight: 500 }}>{success}</Typography>
          </Box>
        ) : (
          <>
            <TextField
              placeholder={t('huoltopaketit.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              size="small"
              autoFocus
              sx={{
                mb: 2,
                mt: 1,
                '& .MuiOutlinedInput-root': { borderRadius: '10px' },
              }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : packages.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {t('huoltopaketit.emptyTitle')}
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflow: 'auto' }}>
                {packages.map((pkg) => (
                  <Box
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg)}
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      border: '1px solid',
                      borderColor: selectedPackage?.id === pkg.id ? 'secondary.main' : 'divider',
                      bgcolor: selectedPackage?.id === pkg.id ? 'action.selected' : 'background.paper',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': {
                        borderColor: 'secondary.main',
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 0.5 }}>
                      {pkg.name}
                    </Typography>
                    {pkg.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {pkg.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        icon={<BuildIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${pkg.job_count || 0} ${t('huoltopaketit.jobsShort')}`}
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        icon={<InventoryIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${pkg.part_count || 0} ${t('huoltopaketit.partsShort')}`}
                        variant="outlined"
                      />
                      {pkg.total_hours && (
                        <Chip
                          size="small"
                          label={`${pkg.total_hours} h`}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              borderRadius: '10px',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': { borderColor: 'divider', bgcolor: 'background.default' },
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={!selectedPackage || applying}
            sx={{
              borderRadius: '10px',
              bgcolor: 'secondary.main',
              fontWeight: 500,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              '&:hover': { bgcolor: 'secondary.light' },
            }}
          >
            {applying ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('huoltopaketit.applyButton')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default ApplyPackageDialog;
